import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaChartPie,
    FaGraduationCap,
    FaSearch,
    FaBriefcase,
    FaUser,
    FaBell,
    FaChevronLeft,
    FaSignOutAlt,
    FaHeadset,
    FaTasks,
    FaFileInvoiceDollar,
    FaClipboardList,
    FaHome
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import useAuthGuard from '../../hooks/useAuthGuard';
import './StudentLayout.css';

const StudentLayout = () => {
    const { user, logout } = React.useContext(AuthContext);
    useAuthGuard(['student']);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [studentName, setStudentName] = useState('Student');
    const [profileImage, setProfileImage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setStudentName(res.data.name || 'Student');
                    setProfileImage(res.data.profileImage || '');
                }
            } catch (err) {
                console.error("Error fetching student profile:", err);
            }
        };
        fetchProfile();

        const handleProfileUpdate = () => {
            fetchProfile();
        };
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const unread = res.data.filter(n => !n.isRead).length;
                setUnreadCount(unread);
            } catch (err) {
                // silent fail
            }
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);

        const handleNotificationsRead = (e) => {
            setUnreadCount(e.detail?.count ?? 0);
        };
        window.addEventListener('notificationsRead', handleNotificationsRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('notificationsRead', handleNotificationsRead);
        };
    }, []);

    const handleLogout = () => {
        logout();
        window.dispatchEvent(new Event('authChange'));
        navigate('/auth');
    };

    const menuGroups = [
        {
            heading: 'Navigation',
            items: [
                { path: '/', name: 'Public Home', icon: <FaHome />, external: true }
            ]
        },
        {
            heading: 'Academic Overview',
            items: [
                { path: '/student-dashboard', name: 'Overview', icon: <FaChartPie /> }
            ]
        },
        {
            heading: 'My Education',
            items: [
                { path: '/student-dashboard/courses', name: 'My Learning', icon: <FaGraduationCap /> },
                { path: '/student-dashboard/assessments', name: 'Assessments & Exams', icon: <FaClipboardList /> }
            ]
        },
        {
            heading: 'Discover & Career',
            items: [
                { path: '/student-dashboard/catalog', name: 'Course Catalog', icon: <FaSearch /> },
                { path: '/student-dashboard/jobs', name: 'Job Portal', icon: <FaBriefcase /> },
                { path: '/student-dashboard/applications', name: 'Applications', icon: <FaTasks /> }
            ]
        },
        {
            heading: 'System & Support',
            items: [
                { path: '/student-dashboard/profile', name: 'Career Profile', icon: <FaUser /> },
                { path: '/student-dashboard/notifications', name: 'Notifications', icon: <FaBell /> },
                { path: '/student-dashboard/invoices', name: 'My Invoices', icon: <FaFileInvoiceDollar /> },
                { path: '/student-dashboard/support', name: 'Support Helpdesk', icon: <FaHeadset /> }
            ]
        }
    ];

    return (
        <div className={`student-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            <aside className="sidebar">
                <button
                    className="sidebar-boundary-toggle"
                    onClick={toggleSidebar}
                    aria-label={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                >
                    <FaChevronLeft />
                </button>

                <div className="sidebar-header">
                    <div className="logo-container">
                        {isSidebarOpen ? (
                            <h2 className="logo-text">ZenX<span className="accent">Student</span></h2>
                        ) : (
                            <h2 className="logo-text">Z<span className="accent">X</span></h2>
                        )}
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="nav-group">
                            <h3 className="nav-heading">{group.heading}</h3>
                            <ul>
                                {group.items.map((item, index) => (
                                    <li key={index}>
                                        <NavLink
                                            to={item.path}
                                            end={item.path === '/student-dashboard'}
                                            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                                            title={!isSidebarOpen ? item.name : ""}
                                        >
                                            <span className="nav-icon">{item.icon}</span>
                                            <span className="nav-text">{item.name}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button className="logout-btn" onClick={handleLogout}>
                        <span className="nav-icon"><FaSignOutAlt /></span>
                        <span className="nav-text">Sign Out</span>
                    </button>
                </div>
            </aside>

            <div className="main-content-wrapper">
                <header className="topbar">
                    <div className="topbar-left">
                        <h2 className="topbar-greeting">Welcome back, {studentName.split(' ')[0]}</h2>
                    </div>

                    <div className="topbar-right">
                        <div
                            className={`notification-trigger ${unreadCount > 0 ? 'has-unread' : ''}`}
                            onClick={() => navigate('/student-dashboard/notifications')}
                        >
                            <FaBell size={18} />
                            {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                        </div>

                        <div className="student-profile-summary" onClick={() => navigate('/student-dashboard/profile')}>
                            <div className="avatar">
                                {profileImage ? (
                                    <img src={`${API_URL}${profileImage}`} alt="Profile" />
                                ) : (
                                    studentName.charAt(0).toUpperCase()
                                )}
                            </div>
                            <div className="profile-text">
                                <span className="name">{studentName}</span>
                                <span className="role">{user?.role?.toUpperCase() || 'STUDENT'}</span>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default StudentLayout;
